import React, { useEffect, useMemo, useState } from 'react';

// NOTE: We intentionally do NOT associate images to specific bed levels/models.
// This carousel is purely a rotating visual showcase.
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/ui/carousel';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const IMAGE_PATHS = [
  '/tanning-beds/ergoline/essence-48.png',
  '/tanning-beds/ergoline/shine.png',
  '/tanning-beds/ergoline/elements.png',
  '/tanning-beds/ergoline/prestige-edge.png',
  '/tanning-beds/ergoline/vitality.png',
  '/tanning-beds/ergoline/spirit.png',
  '/tanning-beds/ergoline/matrix-l33-real.jpg'
];

export const TanningBedCarousel = ({ intervalMs = 4500 }) => {
  const [index, setIndex] = useState(0);
  const [api, setApi] = useState(null);

  const slides = useMemo(
    () =>
      IMAGE_PATHS.map((src, i) => ({
        id: `tanning-bed-slide-${i + 1}`,
        src
      })),
    []
  );

  // Auto-advance by scrolling the carousel itself
  useEffect(() => {
    if (!api) return;

    const t = window.setInterval(() => {
      api.scrollNext();
    }, intervalMs);

    return () => window.clearInterval(t);
  }, [api, intervalMs]);

  // Keep slide counter in sync
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setIndex(api.selectedScrollSnap());
    };

    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  return (
    <section
      className="container mx-auto px-4 -mt-10 relative z-20"
      data-testid="tanning-bed-carousel"
    >
      <Card className="p-4 md:p-6 bg-background/70 backdrop-blur-xl border-border shadow-xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Badge className="bg-[hsl(var(--primary))] text-white" data-testid="tanning-bed-carousel-badge">
                Our Equipment
              </Badge>
              <span className="text-xs text-muted-foreground" data-testid="tanning-bed-carousel-disclaimer">
                Images shown are illustrative.
              </span>
            </div>
            <h2 className="font-serif text-xl md:text-2xl font-bold" data-testid="tanning-bed-carousel-title">
              Get fast results here.
            </h2>
          </div>
          <div className="text-xs text-muted-foreground" data-testid="tanning-bed-carousel-counter">
            Slide {index + 1} of {slides.length}
          </div>
        </div>

        <div className="relative">
          <Carousel
            className="w-full"
            opts={{ align: 'start', loop: true }}
            setApi={setApi}
            data-testid="tanning-bed-carousel-component"
          >
            <CarouselContent>
              {slides.map((s, i) => (
                <CarouselItem key={s.id} className="md:basis-1/2 lg:basis-1/3">
                  <div
                    className={`relative overflow-hidden rounded-xl border bg-muted/40 transition-opacity duration-300 ${
                      i === index ? 'opacity-100' : 'opacity-60'
                    }`}
                    data-testid={s.id}
                  >
                    <div className="aspect-[16/10] bg-gradient-to-br from-slate-950/30 via-slate-900/10 to-transparent">
                      <img
                        src={s.src}
                        alt="Tanning bed product image"
                        className="h-full w-full object-contain p-3"
                        loading="lazy"
                        data-testid={`${s.id}-image`}
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent" />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious data-testid="tanning-bed-carousel-prev" />
            <CarouselNext data-testid="tanning-bed-carousel-next" />
          </Carousel>
        </div>
      </Card>
    </section>
  );
};
